import * as React from 'react';
import { AppSidebar, type NavItem } from './AppSidebar';
import { Header } from './Header';
import type { BreadcrumbItem } from './Header';
import { ScreenHeaderProvider } from './ScreenHeaderContext';
import { BreadcrumbsProvider } from './BreadcrumbsContext';
import { getVersionString } from '@/lib/build-info';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui';
import { SidebarProvider, SidebarInset, useSidebar } from '@/components/ui/sidebar';
import { useSidebarKeyboardShortcut } from './hooks/useSidebarKeyboardShortcut';
import { useSidebarSwipeGesture } from './hooks/useSidebarSwipeGesture';

export interface AppShellProps {
  children: React.ReactNode;
  navigationItems: NavItem[];
  activeNavId: string;
  settingsPath: string;
  breadcrumbs: BreadcrumbItem[];
}

export function AppShell({
  children,
  navigationItems,
  activeNavId,
  settingsPath,
  breadcrumbs: initialBreadcrumbs,
}: AppShellProps) {
  return (
    <SidebarProvider
      className="h-full bg-background text-text-primary"
      style={
        {
          '--sidebar-width': '18rem',
          '--header-height': '56px',
        } as React.CSSProperties
      }
    >
      <AppShellLayout
        navigationItems={navigationItems}
        activeNavId={activeNavId}
        settingsPath={settingsPath}
        breadcrumbs={initialBreadcrumbs}
      >
        {children}
      </AppShellLayout>
    </SidebarProvider>
  );
}

function AppShellLayout({
  children,
  navigationItems,
  activeNavId,
  settingsPath,
  breadcrumbs: initialBreadcrumbs,
}: AppShellProps) {
  const { setOpen, openMobile, setOpenMobile, isMobile } = useSidebar();
  const isDesktop = !isMobile;

  const toggleDesktopCollapsed = React.useCallback(() => {
    setOpen((previous) => !previous);
  }, [setOpen]);

  const toggleMobileSidebarOpen = React.useCallback(() => {
    setOpenMobile((previous) => !previous);
  }, [setOpenMobile]);

  const [actions, setActions] = React.useState<React.ReactNode | null>(null);
  const [baseBreadcrumbs, setBaseBreadcrumbs] =
    React.useState<BreadcrumbItem[]>(initialBreadcrumbs);
  const [breadcrumbs, setBreadcrumbList] = React.useState<BreadcrumbItem[]>(initialBreadcrumbs);

  // Update breadcrumbs when initialBreadcrumbs change
  React.useEffect(() => {
    setBaseBreadcrumbs(initialBreadcrumbs);
    setBreadcrumbList(initialBreadcrumbs);
  }, [initialBreadcrumbs]);

  const setBreadcrumbs = React.useCallback(
    (items: BreadcrumbItem[]) => {
      setBreadcrumbList(items.length ? items : baseBreadcrumbs);
    },
    [baseBreadcrumbs]
  );

  const headerValue = React.useMemo(() => ({ actions, setActions }), [actions, setActions]);

  const breadcrumbsValue = React.useMemo(
    () => ({
      breadcrumbs,
      baseBreadcrumbs,
      setBreadcrumbs,
    }),
    [breadcrumbs, baseBreadcrumbs, setBreadcrumbs]
  );

  React.useEffect(() => {
    return () => setActions(null);
  }, [setActions]);

  const [isScrolled, setIsScrolled] = React.useState(false);
  const versionString = getVersionString();

  useSidebarKeyboardShortcut({
    isDesktop,
    toggleDesktopCollapsed,
    toggleMobileOpen: toggleMobileSidebarOpen,
  });

  useSidebarSwipeGesture({
    isDesktop,
    mobileSidebarOpen: openMobile,
    setMobileSidebarOpen: setOpenMobile,
  });

  return (
    <ScreenHeaderProvider value={headerValue}>
      <BreadcrumbsProvider value={breadcrumbsValue}>
        <Dialog open={openMobile} onOpenChange={setOpenMobile}>
          <DialogContent
            containerClassName="p-0 items-stretch"
            className="h-full w-full max-w-none rounded-none border-0 p-0 shadow-none"
          >
            <AppSidebar
              variant="mobile"
              items={navigationItems}
              activeItem={activeNavId}
              settingsPath={settingsPath}
              className="h-full"
              onNavigate={() => setOpenMobile(false)}
            />
          </DialogContent>
        </Dialog>

        {isDesktop ? (
          <AppSidebar
            variant="desktop"
            items={navigationItems}
            activeItem={activeNavId}
            settingsPath={settingsPath}
            className="hidden sm:flex h-screen sticky top-0"
            sidebarVariant="sidebar"
          />
        ) : null}

        <SidebarInset className="flex h-full w-full min-h-0 min-w-0 flex-col overflow-hidden">
          <div
            data-app-shell-header
            className={cn(
              'z-40 flex h-[var(--header-height)] shrink-0 items-center bg-background transition-shadow duration-200',
              'border-b border-[var(--border-default)]',
              isScrolled ? 'shadow-sm' : undefined
            )}
          >
            <Header breadcrumbs={breadcrumbs} actions={actions} />
          </div>

          <div
            data-app-shell-content
            className="flex min-h-0 flex-1 flex-col overflow-y-auto"
            onScroll={(e) => {
              const target = e.currentTarget;
              const hasScrolled = target.scrollTop > 0;
              setIsScrolled((previous) => (previous !== hasScrolled ? hasScrolled : previous));
            }}
          >
            <main className="w-full min-w-0 flex-1 pb-16 pt-6">
              <div className="mx-auto w-full max-w-[1280px] px-6">{children}</div>
            </main>

            {versionString ? (
              <footer className="shrink-0 pb-6 text-center text-sm font-mono text-brand-blue-300">
                <div className="mx-auto w-full max-w-[1280px] px-6">
                  <p>{versionString}</p>
                </div>
              </footer>
            ) : null}
          </div>
        </SidebarInset>
      </BreadcrumbsProvider>
    </ScreenHeaderProvider>
  );
}

export type { NavItem, BreadcrumbItem };
